import React from "react";
import { Zap, LayoutGrid, BarChart3, Building2, UploadCloud, Sparkles, FileText, Stamp, UserRound, Home, LogOut, ClipboardList, LogIn, Inbox } from "lucide-react";

const navItems = [
  { id: "firm", label: "Firm", icon: Home },
  { id: "requests", label: "Requests", icon: Inbox },
  { id: "workspace", label: "CPA Workspace", icon: LayoutGrid },
  { id: "analytics", label: "KPI Dashboard", icon: BarChart3 },
  { id: "reports", label: "Reports", icon: FileText },
  { id: "signoff", label: "Sign-Off", icon: Stamp },
  { id: "portal", label: "Client Portal", icon: UserRound },
  { id: "admin", label: "Admin" },
];

const Header = ({ view, onSwitchView, entity, entities, onEntityChange, onOpenUpload, onOpenCopilot, auth, isStaff, onSignOut }) => {
  return (
    <header className="glass-panel sticky top-0 z-40 px-6 py-3 flex items-center justify-between border-b border-white/10">
      <div className="flex items-center space-x-8">
        <button onClick={() => onSwitchView("landing")} className="flex items-center space-x-3 group" data-testid="brand-logo">
          <div className="relative flex items-center justify-center w-9 h-9 rounded-md bg-gradient-to-tr from-blue-500 via-indigo-400 to-emerald-400 p-0.5 shadow-glow-blue transition duration-300 transform group-hover:scale-105">
            <div className="w-full h-full bg-zinc-900 rounded-[5px] flex items-center justify-center">
              <Zap className="w-4 h-4 text-blue-400" />
            </div>
          </div>
          <div>
            <span className="font-bold text-lg tracking-tight text-white flex items-center">
              PnLFlow<span className="text-blue-400 font-extrabold">.ai</span>
            </span>
          </div>
        </button>

        {isStaff && (
          <nav className="hidden md:flex items-center space-x-1 bg-white/5 p-1 rounded-md border border-white/10 text-xs font-medium">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = view === item.id;
              return (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => onSwitchView(item.id)}
                  className={`px-3 py-1.5 rounded-md transition flex items-center space-x-1.5 ${
                    active
                      ? "bg-blue-600 text-white font-semibold shadow-sm"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        )}

        {auth && !isStaff && (
          <nav className="hidden md:flex items-center space-x-1 bg-white/5 p-1 rounded-md border border-white/10 text-xs font-medium">
            <button
              data-testid="nav-my-requests"
              onClick={() => onSwitchView("customer")}
              className={`px-3 py-1.5 rounded-md transition flex items-center space-x-1.5 ${
                view === "customer" ? "bg-blue-600 text-white font-semibold" : "text-zinc-400 hover:text-white"
              }`}
            >
              <ClipboardList className="w-3.5 h-3.5" />
              <span>My Requests</span>
            </button>
          </nav>
        )}
      </div>

      <div className="flex items-center space-x-3">
        {isStaff && (
          <>
            <div className="hidden sm:flex items-center space-x-2 bg-zinc-900 px-3 py-1.5 rounded-md border border-white/10">
              <Building2 className="w-3.5 h-3.5 text-blue-400" />
              <select
                data-testid="entity-select"
                value={entity}
                onChange={(e) => onEntityChange(e.target.value)}
                className="bg-transparent text-xs font-semibold text-zinc-300 focus:outline-none cursor-pointer max-w-[200px]"
              >
                {entities.map((ent) => (
                  <option key={ent.slug} className="bg-zinc-900" value={ent.slug}>
                    {ent.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              data-testid="upload-statement-btn"
              onClick={onOpenUpload}
              className="bg-zinc-900 hover:bg-white/5 text-zinc-300 border border-white/10 px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center space-x-1.5"
            >
              <UploadCloud className="w-3.5 h-3.5 text-blue-400" />
              <span className="hidden lg:inline">Upload Statement</span>
            </button>

            <button
              data-testid="ai-assistant-btn"
              onClick={onOpenCopilot}
              className="relative bg-white hover:bg-zinc-200 text-black px-3.5 py-1.5 rounded-md text-xs font-semibold transition-colors flex items-center space-x-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>AI Assistant</span>
            </button>
          </>
        )}

        {auth ? (
          <div className="flex items-center space-x-2">
            <span className="hidden sm:inline text-[11px] text-zinc-500 font-mono" data-testid="auth-user-email">{auth.user.email}</span>
            <button
              data-testid="sign-out-btn"
              onClick={onSignOut}
              className="bg-zinc-900 hover:bg-white/5 text-zinc-300 border border-white/10 px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center space-x-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Sign Out</span>
            </button>
          </div>
        ) : (
          <button
            data-testid="nav-signin"
            onClick={() => onSwitchView("signin")}
            className="bg-white hover:bg-zinc-200 text-black px-3.5 py-1.5 rounded-md text-xs font-semibold transition-colors flex items-center space-x-1.5"
          >
            <LogIn className="w-3.5 h-3.5" />
            <span>Sign In</span>
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;
